import { useState, useMemo } from 'react';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

// Use cases
import { addIdsToImportUrls, filterImportUrlsBySearch } from './tableImportUseCase';

/**
 * @typedef {object} useTableImportReturn
 * @property {boolean} popoverImportIsOpen - Whether the import popover is open
 * @property {(isOpen: boolean) => void} setPopoverImportIsOpen - Set the import popover open state
 * @property {string} importSearchValue - The current search value
 * @property {(value: string) => void} setImportSearchValue - Set the search value
 * @property {Array<{lib: string, options: Array<TableImportUrlsWithId>}>} importUrlsGrouped - The import URLs grouped by optGroup and filtered by search
 */

/**
 * Hook to manage table import functionality
 * @param {object} props
 * @param {Array<ApiManagerTableImportUrls>} [props.importUrls] - The import URLs from the API
 * @returns {useTableImportReturn} Import state and actions
 */
const useTableImport = ({ importUrls = [] }) => {
  const [popoverImportIsOpen, setPopoverImportIsOpen] = useState(false);
  const [importSearchValue, setImportSearchValue] = useState('');

  /**
   * @description The import URLs grouped by optGroup and filtered by search
   * @type {Array<{lib: string, options: Array<TableImportUrlsWithId>}>}
   */
  const importUrlsGrouped = useMemo(() => {
    // Add IDs to items
    const withIds = addIdsToImportUrls(importUrls);

    // Filter by search value
    const filtered = filterImportUrlsBySearch(withIds, importSearchValue);

    // Group by optGroup
    return groupOptionsByOptGroup(filtered);
  }, [importUrls, importSearchValue]);

  return {
    // Popover state
    popoverImportIsOpen,
    setPopoverImportIsOpen,

    // Search
    importSearchValue,
    setImportSearchValue,

    // Data
    importUrlsGrouped,
  };
};

export default useTableImport;
